import { z } from 'zod';
import { bannerContentSchema } from './banner.js';
/**
 * `GET /api/status` — anonymous public status probe. Returns whether the
 * marketplace is in maintenance mode so the SPA can render the
 * maintenance page to anon visitors (`/api/flags` requires `flags:view`
 * and is unreachable by anon callers — this endpoint is the smallest
 * surface that lets every visitor learn the kill-switch state).
 *
 * `publicFlags` is the curated anonymous-safe allowlist — enabled booleans
 * only, never `value` payloads. Each entry exposes a single flag's `enabled`
 * state to unauthenticated callers. This avoids the `/api/flags` 403 problem
 * for features that must gate the UI for all users (e.g. `my-submissions-enabled`).
 *
 * Anonymous reachability is contract-derived (single source: the
 * `anonymous: true` flag on this route's `routeRegistry` entry):
 *   - the API gate resolves it per-request via `isAnonymousOperation`
 *     (`marketplace-api/src/plugins/auth.ts`) — no hand-kept allow-list
 *   - SWA route carveout above the `/api/*` `authenticated` rule in
 *     `marketplace-ui/public/staticwebapp.config.template.json`
 *
 * `version` / `deployedAt` are deferred until bicep wires
 * `MARKETPLACE_VERSION` + `MARKETPLACE_DEPLOYED_AT` — emitting permanently
 * stale `'dev'` strings is worse than not emitting them.
 */
export const getStatusResponseSchema = z
    .object({
    maintenance: z.boolean(),
    publicFlags: z
        .object({
        'my-submissions-enabled': z.boolean(),
        /**
         * Gates the top-level `/metrics` page and its nav entry. Public
         * rather than authed because `/api/flags` requires `flags:view`,
         * which a `metrics_viewer`-only principal does not hold — reading it
         * from the authed flag list would show the flag as permanently off.
         */
        'metrics-dashboard': z.boolean(),
    })
        .strict(),
    /**
     * The sidebar banner to render for every caller, or `null` when disabled
     * or unset. Distinct from `publicFlags` (booleans only) because the banner
     * carries content (title/description/colour). Sourced from the
     * `sidebar-banner` flag's `value`; emitted only when enabled AND the stored
     * value parses as `bannerContentSchema` (fail-safe — never render garbage).
     */
    banner: bannerContentSchema.nullable(),
})
    .strict();
//# sourceMappingURL=status.js.map